/**
 * Return the Home Chat thread that the server recorded as a workflow's entry
 * point. Standalone workflow-chat sessions use the same metadata field, so the
 * entry method must also identify Home Chat before the value is safe to adopt.
 */
export function attributedHomeChatSessionId(
  metadata: unknown,
): string | null {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return null;
  }
  const record = metadata as Record<string, unknown>;
  if (record.is_owner !== true) return null;
  if (record.entry_method !== 'home_chat') return null;
  const sessionId = record.entry_session_id;
  return typeof sessionId === 'string' && sessionId.trim()
    ? sessionId
    : null;
}

/** Prefer the current handoff, durable user choice, attribution, then memory. */
export function resolveWorkflowChatSessionId({
  requestedSessionId,
  metadata,
  persistedSessionId,
  rememberedSessionId,
}: {
  requestedSessionId?: string | null;
  metadata?: unknown;
  persistedSessionId?: string | null;
  rememberedSessionId?: string | null;
}): string | null {
  if (requestedSessionId) return requestedSessionId;
  if (persistedSessionId !== undefined) {
    return persistedSessionId || null;
  }
  return attributedHomeChatSessionId(metadata)
    || rememberedSessionId
    || null;
}

export type PersistWorkflowCompanionSession = (
  flowId: string,
  sessionId: string | null,
) => Promise<void>;
export type SerializedWorkflowCompanionWriter = (
  flowId: string,
  sessionId: string | null,
  isCurrent?: () => boolean,
) => Promise<void>;

/** Preserve browser selection order when companion-session writes overlap. */
export function createSerializedWorkflowCompanionWriter(
  persist: PersistWorkflowCompanionSession,
  onError: (
    error: unknown,
    flowId: string,
    sessionId: string | null,
  ) => void,
): SerializedWorkflowCompanionWriter {
  let pending: Promise<void> = Promise.resolve();

  return (flowId, sessionId, isCurrent) => {
    pending = pending
      .then(() => {
        if (isCurrent && !isCurrent()) return;
        return persist(flowId, sessionId);
      })
      .catch((error) => {
        if (!isCurrent || isCurrent()) {
          onError(error, flowId, sessionId);
        }
      });
    return pending;
  };
}
